/**
 * WebSocket service — Django Channels client.
 * Replaces the old Socket.IO usage.
 *
 * Usage:
 *   import ws from './wsService';
 *   ws.connect(token);
 *   ws.joinTicket('ticket-id');
 *   ws.on('new_message', (payload) => { ... });
 *   ws.off('new_message', handler);
 *   ws.disconnect();
 */

type Handler = (payload: any) => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private token: string | null = null;
  private listeners: Map<string, Set<Handler>> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private shouldReconnect = false;
  private currentRooms: Set<string> = new Set();

  // ── Connection ───────────────────────────────────────────────────────────

  connect(token: string) {
    this.token = token;
    this.shouldReconnect = true;
    this._open();
  }

  disconnect() {
    this.shouldReconnect = false;
    this.currentRooms.clear();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  // ── Room management ──────────────────────────────────────────────────────

  joinDashboard() {
    this._send({ type: 'join_dashboard' });
  }

  joinTicket(ticketId: string) {
    this.currentRooms.add(ticketId);
    this._send({ type: 'join_ticket', ticketId });
  }

  leaveTicket(ticketId: string) {
    this.currentRooms.delete(ticketId);
    this._send({ type: 'leave_ticket', ticketId });
  }

  // ── Event listeners ──────────────────────────────────────────────────────

  on(event: string, handler: Handler) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: string, handler: Handler) {
    this.listeners.get(event)?.delete(handler);
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private _open() {
    if (this.socket && this.socket.readyState < WebSocket.CLOSING) return;

    const hostname = window.location.hostname;
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let host: string;

    if (hostname === 'localhost') {
      // Dev: connect directly to daphne on port 8000
      host = 'localhost:8000';
    } else {
      host = window.location.host;
    }

    const url = `${protocol}://${host}/ws/tickets/?token=${encodeURIComponent(this.token || '')}`;

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.debug('[WS] connected');
      this.reconnectDelay = 2000;
      // Rejoin rooms after reconnect
      this.joinDashboard();
      this.currentRooms.forEach((id) => {
        this._send({ type: 'join_ticket', ticketId: id });
      });
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const eventName = data.event || data.type;
        if (eventName) {
          this._dispatch(eventName, data);
        }
      } catch {
        // ignore parse errors
      }
    };

    this.socket.onclose = (ev) => {
      console.debug('[WS] closed', ev.code);
      this.socket = null;
      if (this.shouldReconnect) {
        this._scheduleReconnect();
      }
    };

    this.socket.onerror = (err) => {
      console.error('[WS] error', err);
    };
  }

  private _send(data: object) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  private _dispatch(event: string, payload: any) {
    this.listeners.get(event)?.forEach((handler) => {
      try {
        handler(payload);
      } catch (e) {
        console.error('[WS] handler error', e);
      }
    });
  }

  private _scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) {
        console.debug('[WS] reconnecting…');
        this._open();
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
      }
    }, this.reconnectDelay);
  }
}

const ws = new WebSocketService();
export default ws;
